import type { FlatSurface } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { type PhotoDimensions, type Rect, surfaceMmToPhoto } from '@/entities/edit-area';
import { MmSizeLabel } from './mm-size-label';

interface DrawPreviewOverlayProps {
  readonly kind: 'rect' | 'ellipse';
  /** 1mm 로 맞춘 표면 mm 사각형. */
  readonly rect: Rect;
  readonly surface: FlatSurface;
  readonly photoSize: PhotoDimensions;
  /** 너무 작아 만들 수 없는 동안 — 회색 점선만 보이고 크기 라벨은 생략한다. */
  readonly muted?: boolean;
}

/** 끄는 중인 사각형·원형의 점선 미리보기와 실제 크기(mm). 포인터 이벤트는 받지 않는다(끌기는 캔버스가 받는다). */
export const DrawPreviewOverlay = ({
  kind,
  rect,
  surface,
  photoSize,
  muted = false,
}: DrawPreviewOverlayProps): ReactElement => {
  const topLeft = surfaceMmToPhoto(surface, photoSize, { xMm: rect.xMm, yMm: rect.yMm });
  const bottomRight = surfaceMmToPhoto(surface, photoSize, {
    xMm: rect.xMm + rect.widthMm,
    yMm: rect.yMm + rect.heightMm,
  });
  const width = bottomRight.x - topLeft.x;
  const height = bottomRight.y - topLeft.y;
  const stroke = {
    fill: muted ? 'rgba(100, 116, 139, 0.08)' : 'rgba(37, 99, 235, 0.08)',
    stroke: muted ? '#64748b' : '#2563eb',
    strokeWidth: 2,
    strokeDasharray: '6 4',
    vectorEffect: 'non-scaling-stroke' as const,
  };
  return (
    <g pointerEvents="none" data-testid="draw-preview">
      {kind === 'ellipse' ? (
        <ellipse
          cx={topLeft.x + width / 2}
          cy={topLeft.y + height / 2}
          rx={width / 2}
          ry={height / 2}
          {...stroke}
        />
      ) : (
        <rect x={topLeft.x} y={topLeft.y} width={width} height={height} {...stroke} />
      )}
      {muted ? null : <MmSizeLabel rect={rect} surface={surface} photoSize={photoSize} />}
    </g>
  );
};
